import {
  ResponsiveContainer, Line, ComposedChart, Bar, BarChart, Cell, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine,
} from "recharts";
import { eur } from "@/lib/api";
import { Card } from "@/components/common";
import { MONTHS, pct, num } from "./helpers";

const kfmt = (v) => `${Math.round(v / 1000)}k`;
export const SEASON_CLR = { alta: "#ea580c", media: "#a16207", bassa: "#64748b" };

function ChartCard({ title, testid, children }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-sm font-semibold text-slate-700 mb-2">{title}</div>
      <div className="overflow-x-auto"><div className="h-64 min-w-[560px]">{children}</div></div>
    </Card>
  );
}

// fondo tratteggiato per mesi anomali (viola) e struttura chiusa (rosa)
const Patterns = () => (
  <defs>
    <pattern id="pv-hatch-anom" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      <rect width="6" height="6" fill="#f5f3ff" /><line x1="0" y1="0" x2="0" y2="6" stroke="#c4b5fd" strokeWidth="2" />
    </pattern>
    <pattern id="pv-hatch-closed" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
      <rect width="6" height="6" fill="#fff1f2" /><line x1="0" y1="0" x2="0" y2="6" stroke="#fda4af" strokeWidth="2" />
    </pattern>
  </defs>
);

const bgFields = (r) => ({ _anom: r.anomalo && !r.closed ? 1 : null, _closed: r.closed ? 1 : null, _season: r.season });

const bgLayers = () => [
  <YAxis key="ybg" yAxisId="bg" hide domain={[0, 1]} />,
  <Bar key="banom" yAxisId="bg" dataKey="_anom" stackId="bg" fill="url(#pv-hatch-anom)" legendType="none" tooltipType="none" isAnimationActive={false} />,
  <Bar key="bclosed" yAxisId="bg" dataKey="_closed" stackId="bg" fill="url(#pv-hatch-closed)" legendType="none" tooltipType="none" isAnimationActive={false} />,
];

function SeasonTick({ x, y, payload, data }) {
  const s = data.find((r) => r.mese === payload.value)?._season;
  return <text x={x} y={y + 12} textAnchor="middle" fontSize={11} fontWeight={s === "bassa" ? 400 : 600} fill={SEASON_CLR[s] || "#64748b"}>{payload.value}</text>;
}

const todayLine = (cur) => (cur >= 0 ? <ReferenceLine x={MONTHS[cur]} stroke="#0f172a" strokeDasharray="4 3" label={{ value: "Oggi", position: "top", fontSize: 11 }} /> : null);
const curIdx = (d) => d.rows.findIndex((r) => r.state === "in_corso");

export function ChartLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 xl:col-span-2" data-testid="pv-chart-legend">
      <span className="inline-flex items-center gap-1"><i className="w-3 h-3 rounded-sm" style={{ background: "repeating-linear-gradient(45deg,#f5f3ff 0 2px,#c4b5fd 2px 4px)" }} /> mese anomalo</span>
      <span className="inline-flex items-center gap-1"><i className="w-3 h-3 rounded-sm" style={{ background: "repeating-linear-gradient(-45deg,#fff1f2 0 2px,#fda4af 2px 4px)" }} /> struttura chiusa</span>
      {Object.entries({ alta: "Alta", media: "Media", bassa: "Bassa" }).map(([k, l]) => (
        <span key={k} className="inline-flex items-center gap-1 font-semibold" style={{ color: SEASON_CLR[k] }}><i className="w-2 h-2 rounded-full" style={{ background: SEASON_CLR[k] }} /> {l} stagione</span>
      ))}
    </div>
  );
}

export function CumulativeChart({ d }) {
  const rows = d.rows, cur = curIdx(d);
  const lastReal = rows.reduce((a, r, i) => (r.state === "chiuso" || r.state === "in_corso" ? i : a), -1);
  const data = rows.map((r, i) => ({
    mese: MONTHS[i], ...bgFields(r),
    Reale: r.state === "non_operativo" || i > lastReal ? null : r.netto_prev_cum,
    Previsto: i >= Math.max(lastReal, 0) && r.state !== "non_operativo" ? r.netto_prev_cum : null,
    Obiettivo: d.target.amount != null ? r.target_cum : null,
    [`${d.year - 1}`]: d.prev_year.nonop[i] ? null : d.prev_year.netto_cum[i],
  }));
  return (
    <ChartCard title="Netto accumulato vs obiettivo" testid="pv-chart-cum">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ left: 8, right: 16, top: 22 }} barCategoryGap={0}>
          <Patterns />
          {bgLayers()}
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="mese" tick={<SeasonTick data={data} />} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={kfmt} />
          <Tooltip formatter={(v) => eur(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine y={0} stroke="#94a3b8" />
          {todayLine(cur)}
          <Line type="monotone" dataKey={`${d.year - 1}`} stroke="#94a3b8" strokeWidth={1} dot={false} connectNulls />
          <Line type="monotone" dataKey="Reale" stroke="#047857" strokeWidth={2.5} dot={{ r: 3 }} />
          <Line type="monotone" dataKey="Previsto" stroke="#7c3aed" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3 }} />
          {d.target.amount != null && <Line type="monotone" dataKey="Obiettivo" stroke="#f59e0b" strokeWidth={2} dot={false} />}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

const ENTRATE_CLR = { chiuso: "#047857", in_corso: "#0284c7", previsto: "#0ea5e9", non_operativo: "#cbd5e1" };
const SPESE_CLR = { chiuso: "#e11d48", in_corso: "#fb7185", previsto: "#fda4af", non_operativo: "#cbd5e1" };

export function MonthlyBarsChart({ d }) {
  const data = d.rows.map((r, i) => ({
    mese: MONTHS[i], state: r.state, ...bgFields(r), Confermato: r.entrate_conf, Pickup: Math.max(r.entrate_prev - r.entrate_conf, 0),
    Spese: r.spese_prev + r.impr_prev, Netto: r.netto_prev,
  }));
  return (
    <ChartCard title="Entrate vs spese mensili (verde = reale, azzurro = confermato, viola = pickup previsto)" testid="pv-chart-bars">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ left: 8, right: 16, top: 22 }}>
          <Patterns />
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="mese" tick={<SeasonTick data={data} />} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={kfmt} />
          <Tooltip formatter={(v) => eur(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {todayLine(curIdx(d))}
          <Bar dataKey="Confermato" stackId="e" fill="#0ea5e9">{data.map((r) => <Cell key={r.mese} fill={ENTRATE_CLR[r.state]} />)}</Bar>
          <Bar dataKey="Pickup" stackId="e" fill="#a78bfa" />
          <Bar dataKey="Spese" fill="#fda4af">{data.map((r) => <Cell key={r.mese} fill={r.closed ? "url(#pv-hatch-closed)" : SPESE_CLR[r.state]} />)}</Bar>
          <Line type="monotone" dataKey="Netto" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

const YEAR_CLR = ["#94a3b8", "#0369a1", "#f59e0b"];

export function OccupancyChart({ d }) {
  const years = Object.keys(d.occupancy_3y);
  const data = MONTHS.map((m, i) => ({ mese: m, ...bgFields(d.rows[i]), ...Object.fromEntries(years.map((y) => [y, d.occupancy_3y[y][i]])) }));
  return (
    <ChartCard title="Occupazione % mensile (prevista; reale per i mesi chiusi)" testid="pv-chart-occ">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ left: 8, right: 16, top: 22 }} barCategoryGap={0}>
          <Patterns />
          {bgLayers()}
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="mese" tick={<SeasonTick data={data} />} />
          <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
          <Tooltip formatter={(v) => pct(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {todayLine(curIdx(d))}
          {years.map((y, i) => <Line key={y} type="monotone" dataKey={y} stroke={YEAR_CLR[i]} strokeWidth={y === String(d.year) ? 2.5 : 1.5} dot={{ r: 2 }} />)}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function PickupChart({ d }) {
  const data = d.pickup.map((p) => ({ mese: `${MONTHS[p.month - 1]} ${String(p.year).slice(2)}`, Oggi: p.oggi, "Stesso giorno anno prec.": p.anno_prec }));
  return (
    <ChartCard title="Pickup: notti in calendario oggi vs stesso giorno dell'anno precedente (prossimi 6 mesi)" testid="pv-chart-pickup">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ left: 8, right: 16, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="mese" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => `${num(v)} notti`} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="Oggi" fill="#0369a1" />
          <Bar dataKey="Stesso giorno anno prec." fill="#94a3b8" />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
